import React, { useEffect, useRef, useState, ReactElement } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import FullscreenControl from '@/components/map/FullscreenControl';
import type { RouteStopPoint } from '@/data/subregionRouteCoords';
import { VotingRecordDots, type VotingRecordEntry } from '@/components/VotingRecordDots';
import { fetchVoterElections } from '@/api/voters';

type LatLng = [number, number];

interface SubregionRouteMapProps {
  /** Ordered stops for one walking shift — each a door with its voter ids. */
  stops: RouteStopPoint[];
  /** Optional label shown in each stop popup, e.g. the shift id. */
  shiftLabel?: string;
  /**
   * Externally-controlled highlighted stop (driven by the house pager in
   * SubregionView). `null` = no house focused: the map fits the whole route.
   * A number pans/zooms to that stop and opens its popup.
   */
  activeStopIndex?: number | null;
  /** Fired when a pin is clicked, so the parent pager can stay in sync. */
  onStopSelect?: (index: number) => void;
}

// Some "doors" are apartment/group addresses with many registered voters.
// Cap how many we show (and how many we fetch) so a popup can't balloon and
// a big door can't fire a request storm; the rest get a "+N more" line.
const MAX_VOTERS_SHOWN = 6;
const MAX_VOTERS_FETCHED = 12;

type DoorCard = { first: string; last: string; record: VotingRecordEntry[] };

/**
 * DoorRecords — fetches identity + voting record live for the voters at one
 * door (by id) when the popup opens, then renders name + G/P/S dots per
 * voter, most-active first. Names/records are NOT in the client bundle; this
 * is the one PII-gated call, so unauthorized roles simply see "unavailable".
 */
function DoorRecords({ voterIds }: { voterIds: number[] }): ReactElement {
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const [cards, setCards] = useState<DoorCard[]>([]);
  const key = voterIds.join(',');

  useEffect(() => {
    if (voterIds.length === 0) {
      setCards([]);
      setStatus('ok');
      return;
    }
    let alive = true;
    setStatus('loading');
    Promise.all(voterIds.slice(0, MAX_VOTERS_FETCHED).map((id) => fetchVoterElections(id)))
      .then((rows) => {
        if (!alive) return;
        const votes = (r: DoorCard) => r.record.filter((e) => e.voted).length;
        const c = rows
          .map((r) => ({ first: r.first_name, last: r.last_name, record: r.elections }))
          .sort(
            (a, b) =>
              votes(b) - votes(a) ||
              `${a.last} ${a.first}`.localeCompare(`${b.last} ${b.first}`),
          );
        setCards(c);
        setStatus('ok');
      })
      .catch(() => {
        if (alive) setStatus('error');
      });
    return () => {
      alive = false;
    };
    // key encodes voterIds; a new door refetches.
  }, [key]);

  if (voterIds.length === 0) {
    return <p className="mt-1 text-xs text-slate-400 italic">no registered voters on file</p>;
  }
  if (status === 'loading') {
    return <p className="mt-2 text-xs text-slate-400 italic">loading voters…</p>;
  }
  if (status === 'error') {
    return <p className="mt-2 text-xs text-slate-400 italic">voting record unavailable</p>;
  }

  const extra = voterIds.length - MAX_VOTERS_SHOWN;
  return (
    <div className="mt-2 space-y-2">
      {cards.slice(0, MAX_VOTERS_SHOWN).map((v, vi) => (
        <div key={vi} className="border-t border-slate-100 pt-1.5 first:border-t-0 first:pt-0">
          <p className="font-semibold text-slate-800 leading-tight">
            {v.first} {v.last}
          </p>
          <div className="mt-1">
            <VotingRecordDots record={v.record} />
          </div>
        </div>
      ))}
      {extra > 0 && (
        <p className="text-[11px] text-slate-500 leading-tight">
          +{extra} more registered at this address
        </p>
      )}
      <p className="text-[10px] text-slate-400 leading-tight">
        ● voted · ○ missed — G general · P primary · S special
      </p>
    </div>
  );
}

/**
 * Numbered pin matching InteractiveMap's route-stop style. The `active` pin
 * (the house the pager is focused on) is larger with a gold ring so it stands
 * out from the rest of the route.
 */
function seqIcon(sequence: number, active = false): L.DivIcon {
  const size = active ? 38 : 30;
  const bg = active ? '#b91c1c' : '#dc2626';
  const ring = active ? '#fbbf24' : '#ffffff';
  const font = active ? 15 : 12;
  return L.divIcon({
    className: 'route-stop-marker',
    html: `
      <div style="
        background-color: ${bg}; border: 3px solid ${ring}; border-radius: 50%;
        width: ${size}px; height: ${size}px; display: flex; align-items: center;
        justify-content: center; color: white; font-size: ${font}px; font-weight: bold;
        box-shadow: 0 2px 6px rgba(0,0,0,0.4);
      ">${sequence}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

/**
 * Owns the map viewport. With no house focused it fits the whole route;
 * when the pager focuses a stop it pans/zooms in and opens that stop's popup.
 * Replaces the old FitBounds so both behaviours share one effect.
 */
function StopFocus({
  activeStopIndex,
  stops,
  markerRefs,
}: {
  activeStopIndex: number | null | undefined;
  stops: RouteStopPoint[];
  markerRefs: React.MutableRefObject<(L.Marker | null)[]>;
}): null {
  const map = useMap();
  useEffect(() => {
    if (stops.length === 0) return;
    if (activeStopIndex == null || !stops[activeStopIndex]) {
      map.fitBounds(
        L.latLngBounds(stops.map((s) => L.latLng(s.lat, s.lng))),
        { padding: [30, 30] },
      );
      return;
    }
    const s = stops[activeStopIndex];
    map.setView([s.lat, s.lng], Math.max(map.getZoom(), 16), { animate: true });
    const marker = markerRefs.current[activeStopIndex];
    if (marker) marker.openPopup();
  }, [activeStopIndex, stops, map]);
  return null;
}

/**
 * Compact in-app Leaflet map for a single static walking shift. Renders the
 * ordered stops as numbered pins connected by a dashed walking line on
 * OpenStreetMap tiles — the in-app alternative to the "Open in Google Maps"
 * hand-off. Each pin's popup fetches the door's registered voters and their
 * recent voting record live (PII-gated). Coords come from
 * src/data/subregionRouteCoords.ts; identity/record are never in the bundle.
 */
export const SubregionRouteMap: React.FC<SubregionRouteMapProps> = ({
  stops,
  shiftLabel,
  activeStopIndex = null,
  onStopSelect,
}): ReactElement | null => {
  // Hooks must run unconditionally, so keep this above the early return.
  const markerRefs = useRef<(L.Marker | null)[]>([]);

  if (!stops || stops.length < 2) return null;

  const line: LatLng[] = stops.map((s) => [s.lat, s.lng]);

  return (
    <div className="h-80 rounded-lg overflow-hidden border border-border-base">
      <MapContainer
        center={line[0]}
        zoom={15}
        style={{ width: '100%', height: '100%' }}
      >
        <FullscreenControl />
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
          maxZoom={19}
        />
        <Polyline
          positions={line}
          pathOptions={{ color: '#dc2626', weight: 4, opacity: 0.85, dashArray: '6 8' }}
        />
        {stops.map((stop, i) => (
          <Marker
            key={i}
            position={[stop.lat, stop.lng]}
            icon={seqIcon(i + 1, i === activeStopIndex)}
            ref={(m) => {
              markerRefs.current[i] = m as unknown as L.Marker | null;
            }}
            eventHandlers={{ click: () => onStopSelect?.(i) }}
          >
            <Popup>
              <div className="text-sm min-w-[13rem]">
                <p className="font-bold text-slate-800">
                  Stop #{i + 1}
                  {shiftLabel && (
                    <span className="ml-1 font-normal text-slate-500">· {shiftLabel}</span>
                  )}
                </p>
                <p className="text-xs text-slate-600">{stop.address}</p>
                <DoorRecords voterIds={stop.voter_ids} />
              </div>
            </Popup>
          </Marker>
        ))}
        <StopFocus
          activeStopIndex={activeStopIndex}
          stops={stops}
          markerRefs={markerRefs}
        />
      </MapContainer>
    </div>
  );
};

export default SubregionRouteMap;
